/**
 * Utilitaires pour la gestion des dates et timezones dans Rintintin Frontend
 *
 * CONVENTION :
 * - Le backend renvoie maintenant des ISO strings avec timezone explicite
 * - Format: "2025-10-29T12:00:00+01:00" (hiver) ou "2025-10-29T12:00:00+02:00" (été)
 * - Ces fonctions permettent de parser correctement ces dates
 *
 * COMPATIBILITÉ :
 * - Les anciennes données (date + time séparés) sont encore supportées
 * - La transition se fait progressivement
 */

/**
 * Parse un datetime ISO avec timezone en objet Date JavaScript
 *
 * Gère correctement les timezones et DST grâce au format ISO avec offset
 *
 * @param isoString - String ISO avec timezone (ex: "2025-10-29T12:00:00+01:00")
 * @returns Date JavaScript avec le bon timezone
 *
 * @example
 * parseISOWithTimezone("2025-10-29T12:00:00+01:00")
 * // Retourne un Date à 12h en heure française (hiver)
 */
export function parseISOWithTimezone(isoString: string): Date {
  if (!isoString) {
    return new Date();
  }

  try {
    // Le parser natif de Date gère correctement les ISO strings avec offset
    const date = new Date(isoString);

    // Vérifier que la date est valide
    if (isNaN(date.getTime())) {
      return new Date();
    }

    return date;
  } catch (error) {
    return new Date();
  }
}

/**
 * Parse une date calendrier (YYYY-MM-DD) sans conversion timezone.
 *
 * À utiliser pour les dates de réservation/session affichées comme un jour
 * métier : le 16 reste le 16, quel que soit le fuseau du navigateur.
 */
export function parseCalendarDate(dateStr: string): Date | null {
  if (!dateStr) return null;

  const dateOnly = String(dateStr).slice(0, 10);
  const [year, month, day] = dateOnly.split('-').map(Number);
  if (!year || !month || !day) return null;

  const date = new Date(year, month - 1, day);
  return isValidDate(date) ? date : null;
}

/** Map i18n language → BCP 47 locale for calendar date formatting. */
export function intlLocaleFromLanguage(language?: string | null): string {
  const lang = String(language || '').toLowerCase();
  if (lang.startsWith('nl')) return lang.includes('be') ? 'nl-BE' : 'nl-NL';
  if (lang.startsWith('en')) return 'en-GB';
  if (lang.startsWith('de')) return 'de-DE';
  if (lang.startsWith('es')) return 'es-ES';
  if (lang.startsWith('it')) return 'it-IT';
  return 'fr-FR';
}

export function formatCalendarDateFR(
  dateStr: string | undefined | null,
  options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  },
  locale: string = 'fr-FR'
): string {
  if (!dateStr) return '';

  const date = parseCalendarDate(dateStr);
  if (!date) return String(dateStr);

  const formatted = new Intl.DateTimeFormat(locale, options).format(date);
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

/**
 * Parse l'ancien format (date + time séparés) en objet Date
 *
 * UTILISÉ POUR LA COMPATIBILITÉ AVEC L'ANCIEN FORMAT
 * À terme, toutes les données utiliseront le nouveau format ISO
 *
 * @param dateStr - Date au format "YYYY-MM-DD"
 * @param timeStr - Heure au format "HH:MM:SS" ou "HH:MM"
 * @returns Date JavaScript
 *
 * @example
 * parseLegacyDateTime("2025-10-29", "12:00:00")
 * // Retourne un Date à 12h avec l'offset correct (hiver ou été)
 */
export function parseLegacyDateTime(dateStr: string, timeStr: string): Date {
  if (!dateStr || !timeStr) {
    return new Date();
  }

  try {
    // Créer une date de référence pour déterminer si on est en DST
    const refDate = new Date(dateStr);

    // Déterminer l'offset (été ou hiver)
    const isDST = isDSTActive(refDate);
    const offset = isDST ? '+02:00' : '+01:00';

    // Normaliser le format de l'heure (enlever les secondes si présentes)
    const timeParts = timeStr.split(':');
    const normalizedTime = `${timeParts[0]}:${timeParts[1]}:00`;

    // Construire un ISO string avec l'offset correct
    const isoString = `${dateStr}T${normalizedTime}${offset}`;

    return parseISOWithTimezone(isoString);
  } catch (error) {
    return new Date();
  }
}

/**
 * Détermine si le DST (Daylight Saving Time / Heure d'été) est actif
 *
 * En France :
 * - Heure d'été (UTC+2) : dernier dimanche de mars 2h → dernier dimanche d'octobre 3h
 * - Heure d'hiver (UTC+1) : reste de l'année
 *
 * Cette fonction utilise une approximation raisonnable basée sur les mois
 *
 * @param date - Date à vérifier
 * @returns true si DST actif (été), false sinon (hiver)
 */
export function isDSTActive(date: Date): boolean {
  const month = date.getMonth() + 1; // getMonth() retourne 0-11
  const day = date.getDate();

  // Simplification : avril à septembre = été garanti
  if (month >= 4 && month <= 9) {
    return true;
  }

  // Novembre à février = hiver garanti
  if (month < 3 || month > 10) {
    return false;
  }

  // Mars : passage à l'heure d'été vers le 25-31
  if (month === 3 && day >= 25) {
    return true;
  }

  // Octobre : passage à l'heure d'hiver vers le 25-31
  if (month === 10 && day < 25) {
    return true;
  }

  // Hiver par défaut
  return false;
}

/**
 * Formate une date pour l'affichage en français
 *
 * @param date - Date à formater
 * @param format - Format souhaité ('full', 'date', 'time')
 * @returns String formaté
 *
 * @example
 * formatDateForDisplay(new Date(), 'full')
 * // "29/10/2025 12:00"
 */
export function formatDateForDisplay(
  date: Date,
  format: 'full' | 'date' | 'time' = 'full'
): string {
  if (!date || isNaN(date.getTime())) {
    return '';
  }

  try {
    if (format === 'full') {
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } else if (format === 'date') {
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } else if (format === 'time') {
      return date.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
      });
    }

    return date.toLocaleString('fr-FR');
  } catch (error) {
    return date.toString();
  }
}

/**
 * Parse une date de réservation depuis l'API
 *
 * Gère automatiquement les deux formats (nouveau et ancien)
 *
 * @param booking - Objet de réservation depuis l'API
 * @returns Objet avec start et end Date
 */
export function parseBookingDateTime(booking: {
  startDateTime?: string;
  endDateTime?: string;
  date?: string;
  startTime?: string;
  endTime?: string;
}): { start: Date; end: Date } {
  // Nouveau format (prioritaire)
  if (booking.startDateTime && booking.endDateTime) {
    return {
      start: parseISOWithTimezone(booking.startDateTime),
      end: parseISOWithTimezone(booking.endDateTime),
    };
  }

  // Ancien format (fallback pour compatibilité)
  if (booking.date && booking.startTime && booking.endTime) {
    return {
      start: parseLegacyDateTime(booking.date, booking.startTime),
      end: parseLegacyDateTime(booking.date, booking.endTime),
    };
  }

  return {
    start: new Date(),
    end: new Date(),
  };
}

/**
 * Valide qu'une date est cohérente
 *
 * @param date - Date à valider
 * @returns true si valide
 */
export function isValidDate(date: Date): boolean {
  return date instanceof Date && !isNaN(date.getTime());
}

/**
 * Compare deux dates (ignore les heures)
 *
 * @param date1 - Première date
 * @param date2 - Deuxième date
 * @returns true si même jour
 */
export function isSameDay(date1: Date, date2: Date): boolean {
  if (!isValidDate(date1) || !isValidDate(date2)) {
    return false;
  }

  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

/**
 * Retourne le nombre de jours entre deux dates
 *
 * @param date1 - Date de début
 * @param date2 - Date de fin
 * @returns Nombre de jours
 */
export function daysBetween(date1: Date, date2: Date): number {
  if (!isValidDate(date1) || !isValidDate(date2)) {
    return 0;
  }

  const oneDay = 24 * 60 * 60 * 1000; // milliseconds
  const diffTime = Math.abs(date2.getTime() - date1.getTime());
  return Math.floor(diffTime / oneDay);
}

/**
 * Retourne le nom du jour en français
 *
 * @param date - Date
 * @returns Nom du jour (ex: "lundi")
 */
export function getDayName(date: Date): string {
  if (!isValidDate(date)) {
    return '';
  }

  const days = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
  return days[date.getDay()];
}

/**
 * Debugging : Log les informations timezone d'une date
 *
 * Utile pour déboguer les problèmes de timezone
 *
 * @param date - Date à analyser
 * @param label - Label pour identifier le log
 */
export function debugTimezone(date: Date, _label: string = 'Date'): void {
  if (!isValidDate(date)) {
    return;
  }

  // Debug information would be logged here in development mode
}

// Export par défaut pour l'usage simple
const dateTimezoneUtils = {
  parseISOWithTimezone,
  parseCalendarDate,
  formatCalendarDateFR,
  intlLocaleFromLanguage,
  parseLegacyDateTime,
  parseBookingDateTime,
  formatDateForDisplay,
  isDSTActive,
  isValidDate,
  isSameDay,
  daysBetween,
  getDayName,
  debugTimezone,
};

export default dateTimezoneUtils;
